// ESAT Specialty Landing Page — Editorial redesign
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import EsatEditorialFooter from './EsatEditorialFooter';
import './EsatLandingPage.css';

import { getBaseApiUrl, getEduPortalUrl } from '../../../config/api';
import { checkoutRefusalMessage, showCheckoutRefusal } from '../../checkout/checkoutRefusal';
import { ORG_PLAN_NOTICE, useStoredUserMayPurchase } from '../../checkout/purchaseEligibility';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the admissions matrix, method cards, sample
// question and school case-study sections).
function ScrollCarousel({ children, ariaLabel }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="esat-scroll-carousel">
      <button
        type="button"
        className={`esat-carousel-arrow esat-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`esat-carousel-arrow esat-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="esat-card-scroll" ref={scrollRef}>
        <div className="esat-card-track">
          {children}
        </div>
      </div>
      <div className="esat-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function EsatLandingPage() {
  const navigate = useNavigate();
  const mayPurchase = useStoredUserMayPurchase();
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleExplorePlans = () => {
    const el = document.getElementById('pricing') || document.getElementById('esat-pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubscribe = async (tierName) => {
    const tierSlug = tierName.toLowerCase().includes('beyond') ? 'esat_beyond' : (tierName.toLowerCase().includes('foundation') ? 'esat_foundations' : 'esat_beginnings');

    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');
    const userRaw = localStorage.getItem('pathbuddy_user') || sessionStorage.getItem('pathbuddy_user') || localStorage.getItem('user');
    const user = (token && userRaw) ? (typeof userRaw === 'string' && userRaw.startsWith('{') ? JSON.parse(userRaw) : { email: userRaw }) : null;

    if (!user || !user.email) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'esat');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=esat`);
      return;
    }

    if (tierSlug === 'esat_beginnings') {
      window.location.href = getEduPortalUrl('/practice/esat');
      return;
    }

    try {
      const apiUrl = getBaseApiUrl();
      const token2 = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');

      const headers = { 'Content-Type': 'application/json' };
      if (token2) {
        headers['Authorization'] = `Bearer ${token2}`;
      }

      const res = await fetch(`${apiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tier_name: tierSlug,
          exam_type: 'esat',
          user_id: user?.id,
          email: user?.email
        })
      });
      const data = await res.json().catch(() => ({}));
      const refusal = checkoutRefusalMessage(res, data);
      if (refusal) {
        showCheckoutRefusal(refusal);
        return;
      }
      if (data.checkoutUrl || data.url) {
        window.location.href = data.checkoutUrl || data.url;
      } else {
        sessionStorage.setItem('pending_checkout_tier', tierSlug);
        sessionStorage.setItem('pending_checkout_exam', 'esat');
        navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=esat`);
      }
    } catch (err) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'esat');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=esat`);
    }
  };

  const esatRequirements = [
    { uni: "University of Cambridge", course: "Engineering (MEng)", requirement: "Mandatory", score: "6.5+ / 9.0", details: "Primary test for interview selection" },
    { uni: "University of Cambridge", course: "Natural Sciences (BA/MSci)", requirement: "Mandatory", score: "6.5+ / 9.0", details: "Maths + 2 Science modules evaluated" },
    { uni: "Imperial College London", course: "Chemical / Aerospace Engineering", requirement: "Mandatory", score: "6.2+ / 9.0", details: "Cutoff score used for interview call" },
    { uni: "Imperial College London", course: "Design Engineering & Physics", requirement: "Mandatory", score: "6.0+ / 9.0", details: "Entrance assessment requirement" },
    { uni: "University of Oxford", course: "Engineering Science (MEng)", requirement: "Highly Recommended", score: "6.5+ / 9.0", details: "Used alongside PAT assessment" },
    { uni: "University College London (UCL)", course: "Electronic & Electrical Eng", requirement: "Mandatory", score: "6.0+ / 9.0", details: "STEM candidate selection grid" },
    { uni: "King's College London (KCL)", course: "General Engineering (BEng/MEng)", requirement: "Recommended", score: "5.8+ / 9.0", details: "Unlocks contextual A-level offer" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained across four foundations spanning cognitive load, deep-structure recognition, heuristic problem-solving and proof-and-refutation, so it reasons through every ESAT module the way a working scientist or engineer would",
        "Finds exactly where a calculation or an argument stops working, so you correct the actual gap — not just the final answer",
        <>The world's first <button type="button" className="esat-inline-link esat-link-btn" onClick={() => navigate('/method/esat')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="esat-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "Four Foundations, One Discipline",
      title: "Four Sciences, Four Traditions",
      meta: "Trained on All Four Deliberately",
      items: [
        "Physics, Chemistry and Biology each reward worked-example fluency and spotting the deep principle beneath an unfamiliar scenario — Sweller and Chi, applied across all three",
        "Mathematics 1 and 2 reward a different pair of skills: recognizing which technique an applied problem is hiding, then checking the answer actually holds — Pólya and Lakatos"
      ]
    },
    {
      tag: "Foundation I",
      title: "Sweller's Cognitive Load",
      meta: "Physics · Chemistry · Biology — 40 min / 27 Q",
      items: [
        "Worked examples reduce the extraneous load of a calculation, so working memory stays free for the actual science being tested",
        "Builds the automaticity 27 questions in 40 minutes demands, rather than re-deriving a method from scratch under the clock",
        "Diagnostics show which routine step still costs you time, so revision closes that gap instead of re-drilling what's already fluent"
      ]
    },
    {
      tag: "Foundation II",
      title: "Chi's Deep-Structure Recognition",
      meta: "Applies Across the Three Science Modules",
      items: [
        "Trains you to categorize a novel problem by the principle it's testing — conservation of energy, a stoichiometric ratio, enzyme saturation — not by its surface dressing",
        "An incline, a titration and a rate graph can all be testing the same underlying principle, and the exam rewards spotting that fast",
        "Mistakes are tagged by the principle missed, not the topic label, so revision closes the actual reasoning gap"
      ]
    },
    {
      tag: "Foundation III",
      title: "Pólya's Heuristic Method",
      meta: "Mathematics 1 · Mathematics 2 — 40 min / 27 Q",
      items: [
        "Understand what's actually being asked before touching a calculation, since a fencing-and-area or a rates-and-distances problem hides its structure in physical language first",
        "Devise a plan by naming the technique the problem depends on, exactly the recognition Mathematics 1 and 2 test under time pressure",
        "Look back once you're done, because in an engineering context a right answer you can't justify isn't actually usable yet"
      ]
    },
    {
      tag: "Foundation IV",
      title: "Lakatos's Logic of Proof",
      meta: "Applies Across Both Mathematics Modules",
      items: [
        "Every answer is checked against what would break it — does the sign make sense, does the second derivative actually confirm a maximum",
        "Trains the habit of checking an answer's order of magnitude against physical intuition, not just its arithmetic",
        "Reasoning tested privately, one student at a time, so the rigor comes without the pressure of defending a method in front of a room of peers"
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Dom Bird · Deputy Head Teacher",
      body: "For close to two years, King Edward VI Handsworth Grammar School for Boys has built its Year 12 STEM applicants' admissions test preparation — the ESAT included — around IVORI, alongside case-based reasoning practice, interview rehearsal and UCAS personal statement support.",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Paul Mann · Head of Careers",
      body: "Clifton College brought IVORI into its Year 12-to-13 transition year, preparing students across the full application cycle for admissions tests and interviews — with the Oxbridge and Imperial engineering cohort sitting the ESAT as a particular focus.",
      detailLabel: "Cohort",
      detailValue: "Year 12 → Year 13",
      detail2Label: "Focus",
      detail2Value: "Oxbridge Admissions"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Extending what's already working at Handsworth Grammar, conversations are underway with the broader King Edward VI Foundation about rolling ESAT preparation out across its family of Birmingham grammar schools.",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "State grammar schools use IVORI for ESAT practice across full Year 12 and 13 cohorts, preparing STEM applicants for engineering and science admissions tests alongside Oxbridge and Imperial interview readiness.",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "ESAT Practice"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Independent schools deploy IVORI for ESAT preparation across Year 12 and 13, supporting STEM applicants heading toward Oxbridge and Imperial engineering, physics and natural sciences admissions.",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "ESAT Practice"
    }
  ];

  const faqs = [
    {
      q: "Which universities require the ESAT for 2026/2027 entry, and what does it test?",
      a: [
        "The ESAT is used by Cambridge and Imperial College London as part of their engineering, natural sciences and physics admissions process, and is referenced by other UK universities including Oxford, where it sits alongside the PAT. Requirements vary by university, course and admissions cycle, so always check the university's current admissions requirements.",
        "The ESAT tests mathematical and scientific problem-solving under time pressure across Mathematics 1, plus whichever two further modules your course requires from Mathematics 2, Physics, Chemistry and Biology — not general subject knowledge recall."
      ]
    },
    {
      q: "What is IVORI's pedagogical method, and who's behind it?",
      a: [
        "Every question, worked solution and mark scheme is reviewed by engineers, scientists and mathematicians from top STEM faculties, alongside educators with formal pedagogical training. That review process is what lets IVORI explain not just the right answer, but why every alternative fails.",
        "IVORI trains across four foundations built for the ESAT's two distinct halves: Sweller's cognitive load theory and Chi's deep-structure recognition for Physics, Chemistry and Biology, and Pólya's heuristic method and Lakatos's logic of proof and refutation for Mathematics 1 and 2. Underneath all four sits IVORI's own foundation decision world model — proprietary technology researched and pioneered in-house by IVORI Lab, and, we believe, the world's first pedagogical AI model built on this kind of foundation.",
        "It's what allows IVORI to reason through a student's working the way a tutor would, rather than simply marking it right or wrong — across a science module's calculation or a mathematics module's applied problem alike."
      ]
    },
    {
      q: "How does IVORI's practice, grading and scoring work?",
      a: [
        "ESAT scores are reported per module on a 1.0-9.0 scale, rather than as a single combined score — so IVORI benchmarks each module you actually sit against your target course's requirement, rather than presenting one number as a universal target.",
        "Every question in IVORI's question bank is multiple choice with full step-by-step working shown, and there is no penalty for a wrong or unanswered answer — so timing strategy never has to include a guessing decision.",
        "Full mocks reproduce the ESAT's exact structure: Mathematics 1 compulsory for every candidate, plus two further modules chosen from Mathematics 2, Physics, Chemistry and Biology, each timed at 40 minutes for 27 questions with no calculator permitted."
      ]
    },
    {
      q: "Can I use IVORI for more than just ESAT preparation?",
      a: ["Yes. IVORI's reasoning practice is designed around transferable skills including worked-example fluency, deep-structure recognition, heuristic problem-solving and rigorous self-checking. ESAT is one of several admissions preparation products within the IVORI ecosystem."]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes. All subscriptions can be managed or cancelled in one click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'esat.ivoriedu.com';
  const onIvoriAiEdu = host === 'esat.edu.ivoriai.com';
  const esatUrl = onIvoriEdu
    ? 'https://esat.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://esat.edu.ivoriai.com/'
      : (host.includes('esat') ? 'https://esat.edu.ivoriai.com/' : 'https://ivoriai.com/edu/esat');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="esat-landing-root">
      <SeoMeta
        title="ESAT Exam Prep 2026 - Engineering and Science Admissions Test"
        description="Master the ESAT (Engineering and Science Admissions Test) for Cambridge and Imperial Engineering & Natural Sciences with ivori timed computer mock tests and detailed science solution breakdowns."
        keywords="ESAT exam prep, ESAT practice test, Cambridge Engineering test, Imperial ESAT, ESAT Maths Physics Chemistry, ivori esat, esat formula sheet pdf, esat specimen paper 2026, cambridge natural sciences esat, imperial physics admissions test, esat past papers free, esat modular test simulator"
        url={esatUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "ESAT Engineering and Science Admissions Test Preparation",
                "description": "Master the Engineering and Science Admissions Test (ESAT) for Cambridge and Imperial with ivori timed computer-based exam simulations across Mathematics, Physics, Chemistry and Biology modules.",
                "provider": { "@type": "Organization", "name": "ivori AI", "url": eduHomeUrl },
                "url": esatUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "Mathematics 1 & 2 Applied Problem Solving",
                  "Physics Worked-Example Fluency",
                  "Chemistry Quantitative Reasoning",
                  "Biology Deep-Structure Recognition"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "ESAT Preparation", "item": esatUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="exam-landing" />

      {/* HERO */}
      <section className="esat-hero-section">
        <div className="esat-container esat-hero-content">
          <span className="esat-mono-eyebrow">IVORI Edu</span>
          <h1 className="esat-hero-title">Master the ESAT.</h1>
          <p className="esat-hero-stat">80% of IVORI students score 6.5+ / 9.0 in their strongest module.</p>
          <p className="esat-hero-tagline">Train the reasoning every ESAT module rewards.</p>
          <button type="button" className="esat-hero-cta" onClick={handleExplorePlans}>
            Choose Tier
          </button>
        </div>
      </section>

      {/* ADMISSIONS GUIDE */}
      <section id="esat-matrix" className="esat-editorial-section">
        <div className="esat-container">
          <div className="esat-editorial-section-head">
            <span className="esat-mono-eyebrow">Target Scores</span>
            <h2>UK University ESAT Admissions Matrix for 2026/2027 Entry</h2>
            <p>Benchmark against Cambridge, Imperial and other leading UK STEM faculties</p>
          </div>

          <ScrollCarousel ariaLabel="faculty">
            {esatRequirements.map((row, idx) => (
              <div className="esat-matrix-card" key={idx}>
                <div className="esat-matrix-uni">{row.uni}</div>
                <div className="esat-matrix-row"><span>Degree Program</span><b>{row.course}</b></div>
                <div className="esat-matrix-row"><span>Requirement</span><b className="esat-req-badge">{row.requirement}</b></div>
                <div className="esat-matrix-row esat-score-cell"><span>Target Score</span><b>{row.score}</b></div>
                <div className="esat-matrix-row"><span>Admissions Note</span><b>{row.details}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* HOW IT WORKS / IVORI METHOD */}
      <section className="esat-editorial-section">
        <div className="esat-container">
          <div className="esat-editorial-section-head">
            <span className="esat-mono-eyebrow">How It Works</span>
            <h2>Utilize IVORI Edu</h2>
            <p>Mathematics 1 is compulsory for every candidate, who then sits two further modules chosen from Mathematics 2, Physics, Chemistry and Biology — trained across four foundations built for exactly that split.</p>
          </div>

          <ScrollCarousel ariaLabel="section">
            {structureCards.map((card, idx) => (
              <div className="esat-structure-card" key={idx}>
                <span className="esat-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="esat-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE QUESTION */}
      <section className="esat-editorial-section">
        <div className="esat-container">
          <div className="esat-editorial-section-head">
            <span className="esat-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how IVORI works through a full worked example from each of ESAT's five modules</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Mathematics 1</span>
              <div className="esat-passage">
                A cyclist travels from A to B at an average speed of 15 km/h, then immediately returns from B to A at an average speed of 10 km/h. The total journey takes 5 hours.
              </div>
              <h3>What is the distance, in km, between A and B?</h3>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Answer Options</span>
              <div className="esat-option">A) 24 km</div>
              <div className="esat-option">B) 27 km</div>
              <div className="esat-option correct">C) 30 km</div>
              <div className="esat-option">D) 33 km</div>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Explanation</span>
              <p>Let the distance be d km. Time out is d/15 hours and time back is d/10 hours, so d/15 + d/10 = 5.</p>
              <p>Multiplying through by 30 gives 2d + 3d = 150, so 5d = 150 and d = 30.</p>
              <p>Options A, B and D come from common slips — averaging the two speeds directly, or an arithmetic error combining the fractions — rather than solving the time equation itself.</p>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Mathematics 2</span>
              <div className="esat-passage">
                A farmer has 40 m of fencing to enclose a rectangular field. One side of the field lies along an existing straight wall, so no fencing is needed on that side.
              </div>
              <h3>What is the maximum area, in m², that can be enclosed?</h3>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Answer Options</span>
              <div className="esat-option">A) 150 m²</div>
              <div className="esat-option">B) 175 m²</div>
              <div className="esat-option correct">C) 200 m²</div>
              <div className="esat-option">D) 225 m²</div>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Explanation</span>
              <p>Let x be the length of each of the two fenced sides perpendicular to the wall. The side parallel to the wall is then 40 − 2x, so area A = x(40 − 2x) = 40x − 2x².</p>
              <p>Differentiating: dA/dx = 40 − 4x. Setting this to zero gives x = 10, so the parallel side is 40 − 20 = 20 m.</p>
              <p>Maximum area = 10 × 20 = 200 m². The second derivative, −4, is negative, confirming this is a maximum rather than a minimum.</p>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Physics</span>
              <div className="esat-passage">
                A ball is thrown horizontally from the top of a 20 m cliff with an initial horizontal speed of 15 m/s. Air resistance is negligible and g = 10 m/s².
              </div>
              <h3>How far from the base of the cliff does the ball land?</h3>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Answer Options</span>
              <div className="esat-option">A) 20 m</div>
              <div className="esat-option">B) 25 m</div>
              <div className="esat-option correct">C) 30 m</div>
              <div className="esat-option">D) 35 m</div>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Explanation</span>
              <p>Vertical and horizontal motion are independent. Time to fall 20 m: 20 = ½ × 10 × t², so t² = 4 and t = 2 s.</p>
              <p>Horizontal distance = horizontal speed × time = 15 × 2 = 30 m.</p>
              <p>The wrong options typically come from using an unrounded g, or from mixing up which equation governs the vertical fall versus the horizontal motion.</p>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Chemistry</span>
              <div className="esat-passage">
                50 cm³ of 0.20 mol dm⁻³ hydrochloric acid is exactly neutralised by 25 cm³ of sodium hydroxide solution: HCl + NaOH → NaCl + H₂O.
              </div>
              <h3>What is the concentration of the sodium hydroxide solution?</h3>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Answer Options</span>
              <div className="esat-option">A) 0.20 mol dm⁻³</div>
              <div className="esat-option correct">B) 0.40 mol dm⁻³</div>
              <div className="esat-option">C) 0.50 mol dm⁻³</div>
              <div className="esat-option">D) 0.80 mol dm⁻³</div>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Explanation</span>
              <p>Moles of HCl = 0.050 dm³ × 0.20 mol dm⁻³ = 0.010 mol. The 1:1 equation means moles of NaOH are also 0.010 mol.</p>
              <p>Concentration of NaOH = moles ÷ volume = 0.010 mol ÷ 0.025 dm³ = 0.40 mol dm⁻³.</p>
              <p>A is the acid's own concentration copied across by mistake; C and D come from dividing by the wrong volume or forgetting to convert cm³ to dm³ before dividing.</p>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Biology</span>
              <div className="esat-passage">
                An enzyme-catalysed reaction is run at a fixed enzyme concentration while substrate concentration is steadily increased. Reaction rate rises at first, then levels off even as substrate concentration keeps increasing.
              </div>
              <h3>Which of the following best explains why the rate plateaus?</h3>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Answer Options</span>
              <div className="esat-option">A) The enzyme is denatured by the excess substrate.</div>
              <div className="esat-option correct">B) All available active sites are saturated with substrate, so adding more substrate has no further effect.</div>
              <div className="esat-option">C) The reaction has reached chemical equilibrium.</div>
              <div className="esat-option">D) The substrate begins to inhibit the enzyme competitively.</div>
            </div>

            <div className="esat-qcard-panel">
              <span className="esat-mono-tag">Explanation</span>
              <p>With enzyme concentration fixed, there is a limited number of active sites available at any moment.</p>
              <p>Once substrate concentration is high enough that every active site is occupied continuously, rate is limited by how fast enzyme-substrate complexes can form products — adding more substrate can't speed that up further.</p>
              <p>A and D would need direct evidence of denaturation or inhibition, which the scenario doesn't describe; C misapplies an equilibrium concept to what is fundamentally a rate-limited enzyme process.</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="esat-editorial-section">
        <div className="esat-container">
          <div className="esat-editorial-section-head">
            <span className="esat-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools</h2>
            <p>Deployed across top UK grammar and independent schools with ESAT cohorts</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="esat-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="esat-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="esat-school-more">
                  Find out more about{' '}
                  <button type="button" className="esat-inline-link esat-link-btn" onClick={() => navigate('/cases')}>
                    {school.name}
                  </button>
                </p>
                <div className="esat-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="esat-editorial-section esat-pricing-editorial">
        <div className="esat-container">
          <div className="esat-editorial-section-head">
            <span className="esat-mono-eyebrow">Prep Tiers</span>
            <h2>Select Your Tier</h2>
            <p>Choose your level and go deeper when you're ready to improve your score</p>
            {!mayPurchase && <p>{ORG_PLAN_NOTICE}</p>}
          </div>

          <div className="esat-pricing-grid">
            <div className="esat-plan">
              <h3>Beginnings</h3>
              <div className="esat-plan-price">
                <span className="amount">£0</span> <span className="period">free forever</span>
              </div>
              <button type="button" className="esat-plan-btn" onClick={() => handleSubscribe('ESAT Beginnings')}>
                Get Started
              </button>
              <ul>
                <li><span className="esat-check">·</span> <b>All Content for Free</b></li>
                <li><span className="esat-check">·</span> 4 Official Tests / 182 Questions</li>
                <li><span className="esat-check">·</span> 19 Easy Tests / 855 Questions</li>
                <li><span className="esat-check">·</span> 350+ Engineering &amp; Physical Sciences Cases &amp; Tutorials</li>
              </ul>
            </div>

            <div className="esat-plan">
              <h3>Foundations</h3>
              <div className="esat-plan-price">
                <span className="amount">£60</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="esat-plan-btn" onClick={() => handleSubscribe('ESAT Foundations')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="esat-check">·</span> <b>All content in Beginnings</b></li>
                <li><span className="esat-check">·</span> 12 Standard Tests / 540 Questions</li>
                <li><span className="esat-check">·</span> 250+ Applied Problem-Solving Questions</li>
                <li><span className="esat-check">·</span> UCAS Personal Statement Reviewer</li>
              </ul>
            </div>

            <div className="esat-plan featured">
              <span className="esat-plan-badge">Recommended · Oxbridge</span>
              <h3>Beyond</h3>
              <div className="esat-plan-price">
                <span className="amount">£80</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="esat-plan-btn" onClick={() => handleSubscribe('ESAT Beyond')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="esat-check">·</span> <b>All content in Foundations</b></li>
                <li><span className="esat-check">·</span> 4 Hard Tests / 180 Questions</li>
                <li><span className="esat-check">·</span> 200+ Deep-Structure Practice Questions</li>
                <li><span className="esat-check">·</span> 16 Oxbridge Interview Simulations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="esat-editorial-section esat-faq-editorial">
        <div className="esat-container">
          <div className="esat-editorial-section-head">
            <span className="esat-mono-eyebrow">Got Questions?</span>
            <h2>FAQs</h2>
            <p>Find answers on prep tiers, scoring and how IVORI's feedback works</p>
          </div>

          <div className="esat-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`esat-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="esat-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="esat-faq-plus">+</span>
                </div>
                <div className="esat-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <EsatEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}